import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

const emptyState = cva('w-full flex flex-col items-center text-center', {
  variants: {
    variant: {
      /** Standalone card with dashed border — page-level empty states. */
      card: 'rounded-2xl border border-dashed border-border bg-card/40 px-6 py-16',
      /** No border/background, tighter padding — popovers, panels, dropdowns. */
      plain: 'px-6 py-8',
    },
  },
  defaultVariants: { variant: 'card' },
});

type KoobEmptyStateProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'title'
> &
  VariantProps<typeof emptyState> & {
    /** Rendered inside the brand-tinted bubble. Size it at the call site
     *  (svg h-7 w-7, font icons text-2xl); color is inherited. */
    icon?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    /** Action slot — pass a KoobButton. */
    children?: React.ReactNode;
  };

const KoobEmptyState = React.forwardRef<HTMLDivElement, KoobEmptyStateProps>(
  ({ variant, icon, title, description, children, ...props }, ref) => (
    <div ref={ref} className={emptyState({ variant })} {...props}>
      {icon && (
        <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center mb-4 [&_svg]:h-7 [&_svg]:w-7">
          {icon}
        </div>
      )}
      {title && (
        <h2 className="m-0 text-lg font-semibold text-foreground">{title}</h2>
      )}
      {description && (
        <p className="m-0 mt-1 max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {children && <div className="mt-5">{children}</div>}
    </div>
  ),
);

KoobEmptyState.displayName = 'KoobEmptyState';

export default KoobEmptyState;
